// Drag and drop with pointer events (mouse, pen and touch), from the palette or inside the canvas.
// The drop spot is computed from the pointer on every move; no drop zone is ever added to the content.
import { blockOf, canContain, getBlock } from './registry.js';
import { h, icon } from './dom.js';
import { uiIcons } from '../ui/icons.js';

const EDGE = 12; // px from an edge where a drop goes next to a block instead of inside it
const SCROLL_ZONE = 48;

export class DragController {
    constructor(editor) {
        this.editor = editor;
        this.active = null;
    }

    /** Starts dragging a new block from the palette (event in the host document). */
    startNew(item, event, source) {
        this.begin({ type: 'new', name: item.block, item }, event, source, false);
    }

    /** Starts moving a block of the canvas (event in the canvas document). */
    startMove(el, event, source) {
        const def = blockOf(el);
        if (!def) return;
        this.editor.text.deactivate();
        this.begin({ type: 'move', name: def.name, el }, event, source, true);
    }

    begin(drag, event, source, inCanvas) {
        const e = this.editor;
        const def = getBlock(drag.name);
        const label = drag.item ? e.t(drag.item.label) : e.t(def.label);
        this.ghost = h('div', { class: 'pf-ghost' }, icon(uiIcons[(drag.item && drag.item.icon) || def.icon] || ''), h('span', {}, label));
        document.body.append(this.ghost);
        this.active = { ...drag, inCanvas, source, pointerId: event.pointerId, spot: null, startX: event.clientX, startY: event.clientY, moved: false };
        if (drag.type === 'move') drag.el.classList.add('pf-dragging');
        e.canvas.doc.body.classList.add('pf-is-dragging');
        e.container.classList.add('pf-is-dragging');
        try {
            source.setPointerCapture(event.pointerId);
        } catch (error) {
            // Synthetic events (tests, keyboard) have no capturable pointer.
        }
        this.onMove = moveEvent => this.move(moveEvent);
        this.onUp = upEvent => this.end(upEvent, false);
        this.onCancel = cancelEvent => this.end(cancelEvent, true);
        this.onKey = keyEvent => {
            if (keyEvent.key === 'Escape') this.end(keyEvent, true);
        };
        source.addEventListener('pointermove', this.onMove);
        source.addEventListener('pointerup', this.onUp);
        source.addEventListener('pointercancel', this.onCancel);
        source.ownerDocument.addEventListener('keydown', this.onKey);
        this.move(event);
    }

    /** Pointer position in canvas coordinates, or null when outside the canvas. */
    canvasPoint(event) {
        const a = this.active;
        const rect = this.frameRect();
        const scale = this.editor.app.scale || 1; // the canvas may be scaled down to fit the stage
        if (a.inCanvas) return { x: event.clientX, y: event.clientY, hostX: rect.left + event.clientX * scale, hostY: rect.top + event.clientY * scale };
        const x = (event.clientX - rect.left) / scale;
        const y = (event.clientY - rect.top) / scale;
        const inside = event.clientX >= rect.left && event.clientY >= rect.top && event.clientX <= rect.right && event.clientY <= rect.bottom;
        return { x, y, hostX: event.clientX, hostY: event.clientY, outside: !inside };
    }

    frameRect() {
        return this.editor.canvas.iframe.getBoundingClientRect();
    }

    move(event) {
        const a = this.active;
        if (!a) return;
        const point = this.canvasPoint(event);
        if (Math.abs(event.clientX - a.startX) + Math.abs(event.clientY - a.startY) > 4) a.moved = true;
        this.ghost.style.transform = `translate(${Math.round(point.hostX + 14)}px, ${Math.round(point.hostY + 14)}px)`;
        if (point.outside) {
            a.spot = null;
            this.editor.overlay.showDrop(null);
            return;
        }
        this.autoScroll(point.y);
        a.spot = this.findSpot(point.x, point.y);
        this.editor.overlay.showDrop(a.spot);
    }

    autoScroll(y) {
        const win = this.editor.canvas.win;
        const height = win.innerHeight;
        if (y < SCROLL_ZONE) win.scrollBy(0, -Math.ceil((SCROLL_ZONE - y) / 3));
        else if (y > height - SCROLL_ZONE) win.scrollBy(0, Math.ceil((y - (height - SCROLL_ZONE)) / 3));
    }

    accepts(container, name) {
        const root = this.editor.canvas.root;
        return container === root ? canContain(null, name, true) : canContain(container, name);
    }

    axisOf(container) {
        const def = blockOf(container);
        return def && def.kind === 'row' ? 'x' : 'y';
    }

    containerOf(el) {
        const root = this.editor.canvas.root;
        let parent = el.parentElement;
        while (parent && parent !== root && !parent.hasAttribute('data-pf')) parent = parent.parentElement;
        return parent || root;
    }

    /** Where a block would go inside `container` for a pointer at (x, y). */
    indexSpot(container, x, y, moving = null) {
        const axis = this.axisOf(container);
        const children = Array.from(container.children).filter(child => child.hasAttribute('data-pf') && child !== moving);
        for (const child of children) {
            const r = child.getBoundingClientRect();
            const before = axis === 'x'
                ? y < r.top || (y <= r.bottom && x < r.left + r.width / 2)
                : y < r.top + r.height / 2;
            if (before) return { parent: container, before: child, axis, moving };
        }
        return { parent: container, before: null, axis, moving };
    }

    /** Where a block named `name` would go for a pointer at (x, y); during a drag, the dragged block. */
    findSpot(x, y, name = this.active && this.active.name, moving = this.active ? this.active.el || null : null) {
        const e = this.editor;
        const root = e.canvas.root;
        let hit = e.canvas.doc.elementFromPoint(x, y);
        if (!hit || hit.closest('.pf-ui')) return this.active ? this.active.spot : null;
        let current = root.contains(hit) ? hit.closest('[data-pf]') : null;
        if (current && !root.contains(current)) current = null;
        if (moving && current && moving.contains(current)) current = moving;

        while (current && current !== root) {
            const parent = this.containerOf(current);
            const axis = this.axisOf(parent);
            const r = current.getBoundingClientRect();
            const nearEdge = axis === 'x'
                ? x - r.left < EDGE || r.right - x < EDGE
                : y - r.top < EDGE || r.bottom - y < EDGE;
            const inside = current !== moving && this.accepts(current, name);
            const beside = this.accepts(parent, name);
            if (inside && (!nearEdge || !beside)) return this.indexSpot(current, x, y, moving);
            if (beside) {
                const after = axis === 'x' ? x > r.left + r.width / 2 : y > r.top + r.height / 2;
                let before = after ? current.nextElementSibling : current;
                while (before && (!before.hasAttribute('data-pf') || before === moving)) before = before.nextElementSibling;
                return { parent, before, axis, moving };
            }
            current = parent;
        }
        return this.accepts(root, name) ? this.indexSpot(root, x, y, moving) : null;
    }

    end(event, cancelled) {
        const a = this.active;
        if (!a) return;
        const e = this.editor;
        a.source.removeEventListener('pointermove', this.onMove);
        a.source.removeEventListener('pointerup', this.onUp);
        a.source.removeEventListener('pointercancel', this.onCancel);
        a.source.ownerDocument.removeEventListener('keydown', this.onKey);
        try {
            a.source.releasePointerCapture(a.pointerId);
        } catch (error) {
            // already released
        }
        this.ghost.remove();
        this.ghost = null;
        if (a.el) a.el.classList.remove('pf-dragging');
        e.canvas.doc.body.classList.remove('pf-is-dragging');
        e.container.classList.remove('pf-is-dragging');
        e.overlay.showDrop(null);
        this.active = null;

        if (cancelled) return;
        // A click on a palette item without moving inserts the block at the selection.
        if (a.type === 'new' && !a.moved) {
            e.insertItem(a.item);
            return;
        }
        if (!a.spot) return;
        if (a.type === 'new') e.insertItem(a.item, a.spot);
        else e.moveBlock(a.el, a.spot);
    }
}
